"use client";

import { useEffect, useState } from "react";
import { useTaskContext } from "@/contexts/TaskContext";
import { useBotworkContext } from "@/contexts/BotworkContext";
import type { DemoAgent } from "@/lib/app-types";

import ConversionGate from "@/components/web/ConversionGate";

import Sidebar from "@/components/app/Sidebar";
import ChatCanvas from "@/components/app/ChatCanvas";
import ConversationHistory from "@/components/app/ConversationHistory";
import PhaseView from "@/components/app/PhaseView";
import InputBar from "@/components/app/InputBar";
import QuestionCard from "@/components/app/QuestionCard";
import BudgetCard, { type BudgetMode } from "@/components/app/BudgetCard";
import TierSelector from "@/components/app/TierSelector";
import FormatSelector from "@/components/app/FormatSelector";
import LoginModal from "@/components/app/LoginModal";
import AgentBrowserModal from "@/components/app/AgentBrowserModal";
import AgentProfilePanel from "@/components/app/AgentProfilePanel";
import CompletedBanner from "@/components/app/CompletedBanner";
import ThinkingIndicator from "@/components/app/ThinkingIndicator";
import ConsentBanner from "@/components/app/ConsentBanner";
import UserMessage from "@/components/app/messages/UserMessage";
import SystemMessage from "@/components/app/messages/SystemMessage";
import { useToast } from "@/components/app/Toast";
import { colors } from "@/styles/design-tokens";

const BUDGET_TIERS = [10, 25, 50, 100];

const POSITIVE_KEYWORDS = [
  "thanks",
  "thank you",
  "great",
  "perfect",
  "looks good",
  "awesome",
  "love it",
  "amazing",
  "nice",
  "good job",
];
const NEGATIVE_KEYWORDS = [
  "change",
  "fix",
  "wrong",
  "update",
  "redo",
  "revise",
  "not what i wanted",
  "not right",
  "different",
  "rewrite",
];

type Suggestion = "approve" | "revise" | null;
type Intent = "positive" | "revise" | "question";

interface FollowupMessage {
  id: number;
  role: "user" | "agent";
  text: string;
  thinking?: boolean;
}

function detectSuggestion(text: string): Suggestion {
  const lower = text.toLowerCase();
  if (POSITIVE_KEYWORDS.some((w) => lower.includes(w))) return "approve";
  if (NEGATIVE_KEYWORDS.some((w) => lower.includes(w))) return "revise";
  return null;
}

function detectIntent(text: string): Intent {
  const lower = text.toLowerCase();
  if (POSITIVE_KEYWORDS.some((w) => lower.includes(w))) return "positive";
  if (NEGATIVE_KEYWORDS.some((w) => lower.includes(w))) return "revise";
  return "question";
}

function agentReplyFor(intent: Intent, agentName: string): string {
  switch (intent) {
    case "positive":
      return "Thank you! Let me know if you need anything else.";
    case "revise":
      return "I'll work on those changes. Give me a moment…";
    case "question":
    default:
      return `Great question! Based on ${agentName}'s analysis, the key finding still holds — happy to dig deeper into any specific section.`;
  }
}

export default function AppPage() {
  const ctx = useTaskContext();
  const botwork = useBotworkContext();
  const toast = useToast();
  // browserOpen drives the in-chat "Browse all agents" modal, opened
  // by onBrowseAll from inline UI (agent matching screen). The
  // sidebar's Marketplace button now navigates to /app/marketplace
  // instead of triggering this modal — the cross-page event/URL
  // mechanism that used to feed it has been removed.
  const [browserOpen, setBrowserOpen] = useState(false);
  const [profileAgent, setProfileAgent] = useState<DemoAgent | null>(null);
  const [profileClosing, setProfileClosing] = useState(false);
  const [followupText, setFollowupText] = useState("");
  const [followups, setFollowups] = useState<FollowupMessage[]>([]);
  const [budgetTier, setBudgetTier] = useState<number>(25);
  const [budgetMode, setBudgetMode] = useState<BudgetMode>("task");

  const closeProfile = () => {
    if (!profileAgent) return;
    setProfileClosing(true);
    setTimeout(() => {
      setProfileAgent(null);
      setProfileClosing(false);
    }, 250);
  };

  const handleSubmit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (ctx.phase === "idle") {
      ctx.dispatch({ type: "SUBMIT_TASK", description: trimmed });
      return;
    }
    if (ctx.phase === "delivered") {
      const agentName = ctx.task?.selectedAgent?.name ?? "the agent";
      const intent = detectIntent(trimmed);
      const userId = Date.now();
      const agentId = userId + 1;

      setFollowups((prev) => [
        ...prev,
        { id: userId, role: "user", text: trimmed },
        { id: agentId, role: "agent", text: "", thinking: true },
      ]);
      setFollowupText("");

      setTimeout(() => {
        const reply = agentReplyFor(intent, agentName);
        setFollowups((prev) =>
          prev.map((m) => (m.id === agentId ? { ...m, text: reply, thinking: false } : m)),
        );

        // If the user asked for a revision, trigger the revision flow
        // after a short beat so they see the agent ack first.
        if (intent === "revise" && ctx.task && ctx.task.revisionCount < 3) {
          setTimeout(() => ctx.dispatch({ type: "REQUEST_REVISION" }), 700);
        }
      }, 1500);
      return;
    }
  };

  const handleSuggestionClick = (kind: "approve" | "revise") => {
    if (kind === "approve") {
      ctx.approveCurrentTask(ctx.task?.rating ?? 0).catch(() => {});
    } else if (ctx.task && ctx.task.revisionCount < 3) {
      ctx.dispatch({ type: "REQUEST_REVISION" });
    }
    setFollowupText("");
  };

  // Phases where the input bar row is rendered at all.
  // Clarifying / budget / tier / format swap the row for their respective cards.
  // Completed swaps it for CompletedBanner.
  const showInputBar =
    ctx.phase === "idle" ||
    ctx.phase === "submitted" ||
    ctx.phase === "matching" ||
    ctx.phase === "selecting" ||
    ctx.phase === "confirmed" ||
    ctx.phase === "working" ||
    ctx.phase === "delivered" ||
    ctx.phase === "approved" ||
    ctx.phase === "rating";

  // Seed the budget tier with the recommended value each time we enter the
  // budget phase. Otherwise previously selected tier would persist on revisit.
  const recommendedBudget = ctx.task?.recommendedBudget ?? 25;
  useEffect(() => {
    if (ctx.phase === "budget") setBudgetTier(recommendedBudget);
  }, [ctx.phase, recommendedBudget]);

  // Clear follow-up messages when leaving the delivered phase (new round of
  // revisions, approved+rating, or full reset). They live only alongside
  // the current delivered result.
  useEffect(() => {
    if (ctx.phase !== "delivered") setFollowups([]);
  }, [ctx.phase]);

  // Input is editable only in idle (start new task) or delivered (follow-up).
  const inputDisabled = !(ctx.phase === "idle" || ctx.phase === "delivered");

  const placeholder = (() => {
    if (ctx.phase === "delivered") return "Reply to continue…";
    if (ctx.phase === "working" || ctx.phase === "confirmed" || ctx.phase === "matching") {
      return "Working on your task…";
    }
    if (ctx.phase === "submitted") return "Analyzing…";
    if (ctx.phase === "approved" || ctx.phase === "rating") return "Rating…";
    if (ctx.phase === "selecting") return "Pick an agent above…";
    return "Type a task or question…";
  })();

  const suggestion: Suggestion = ctx.phase === "delivered" ? detectSuggestion(followupText) : null;

  const completedFooter = ctx.phase === "completed";

  const profileData = profileAgent
    ? {
        name: profileAgent.name,
        initials: profileAgent.avatarInitials,
        avatarBg: profileAgent.avatarBg,
        status: "AVAILABLE NOW",
        rating: profileAgent.rating,
        tasksCompleted: profileAgent.tasksCompleted,
        description: profileAgent.description,
        specialties: profileAgent.specialties,
        pricingRange: profileAgent.priceRange,
        stats: profileAgent.stats,
        reviews: profileAgent.reviews,
      }
    : null;

  return (
    <div className="h-screen flex app-shell-root" style={{ background: colors.bg }}>
      <Sidebar />

      <main
        className={`flex-1 flex flex-col overflow-hidden relative app-main-area ${profileData ? "app-profile-open" : ""}`}
        style={{ background: colors.mainArea, transition: "all 200ms ease-out" }}
      >
        {botwork.mode === "live" && botwork.connectionState !== "connected" && botwork.connectionState !== "authenticated" && (
          <ReconnectBanner state={botwork.connectionState} />
        )}
        <ChatCanvas>
          <ConsentBanner />
          <ConversationHistory />
          {ctx.gateRequired ? (
            <ConversionGate
              pendingPrompt={ctx.task?.description ?? ""}
              onDismiss={() => ctx.dispatch({ type: "CLEAR_GATE" })}
            />
          ) : ctx.phase !== "completed" ? (
            <PhaseView
              onSubmit={handleSubmit}
              onBrowseAll={() => setBrowserOpen(true)}
              onAgentCardClick={(agent) => setProfileAgent(agent)}
            />
          ) : null}
          {ctx.phase === "delivered" && followups.length > 0 && (
            <FollowupThread messages={followups} />
          )}
        </ChatCanvas>

        <div
          className="shrink-0 flex flex-col items-center"
          style={{ padding: "0 32px 12px", width: "100%" }}
        >
          {completedFooter && ctx.task && ctx.task.selectedAgent ? (
            <CompletedBanner
              taskTitle={ctx.task.description}
              agentName={ctx.task.selectedAgent.name}
              cost={ctx.task.budget ?? 0}
              rating={ctx.task.rating}
              onNewTask={ctx.finalizeAndReset}
              onRetry={() => {
                const desc = ctx.task?.description;
                ctx.finalizeAndReset();
                if (desc) {
                  setTimeout(() => ctx.dispatch({ type: "SUBMIT_TASK", description: desc }), 0);
                }
              }}
            />
          ) : ctx.phase === "clarifying" && ctx.task ? (
            (() => {
              const q = ctx.task.questions[ctx.task.currentQIdx];
              if (!q) return null;
              return (
                <QuestionCard
                  questionNumber={ctx.task.currentQIdx + 1}
                  totalQuestions={ctx.task.questions.length}
                  title={q.title}
                  choices={q.choices}
                  onAnswer={(idx) =>
                    ctx.dispatch({ type: "ANSWER_QUESTION", answer: q.choices[idx].text })
                  }
                  onTextAnswer={(text) => ctx.dispatch({ type: "ANSWER_QUESTION", answer: text })}
                  onSkip={() => ctx.dispatch({ type: "SKIP_QUESTION" })}
                  onSkipAll={() => ctx.dispatch({ type: "SKIP_ALL_QUESTIONS" })}
                />
              );
            })()
          ) : ctx.phase === "budget" && ctx.task ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, width: "100%" }}>
              {ctx.errorMessage && (
                <div
                  style={{
                    width: 720,
                    maxWidth: "100%",
                    background: "rgba(248,113,113,0.1)",
                    border: "1px solid rgba(248,113,113,0.4)",
                    borderRadius: 12,
                    padding: "12px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    color: "#FCA5A5",
                    fontSize: 14,
                  }}
                >
                  <span>{ctx.errorMessage}</span>
                  <button
                    onClick={ctx.resetBalance}
                    className="app-btn-primary"
                    style={{
                      background: "#8635F6",
                      border: "none",
                      borderRadius: 9999,
                      padding: "6px 14px",
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Add demo funds
                  </button>
                </div>
              )}
              <BudgetCard
                recommended={ctx.task.recommendedBudget}
                tiers={BUDGET_TIERS}
                selected={budgetTier}
                onTierSelect={setBudgetTier}
                mode={budgetMode}
                onModeChange={setBudgetMode}
                onConfirm={() => {
                  // Hourly mode uses the high-end estimate ($15 x 3h = $45).
                  const amount = budgetMode === "hourly" ? 45 : budgetTier;
                  ctx.confirmBudget(amount);
                }}
              />
            </div>
          ) : ctx.phase === "tier" ? (
            <TierSelector
              onSelect={(tier) => ctx.dispatch({ type: "SELECT_TIER", tier })}
            />
          ) : ctx.phase === "format" ? (
            <FormatSelector
              category={ctx.task?.category ?? null}
              onSelect={(format, downloadKey) => ctx.dispatch({ type: "SELECT_FORMAT", format, downloadKey })}
            />
          ) : showInputBar ? (
            <div style={{ width: "100%", maxWidth: 720, display: "flex", flexDirection: "column", gap: 8 }}>
              {suggestion && (
                <SuggestionPill kind={suggestion} onClick={() => handleSuggestionClick(suggestion)} />
              )}
              <InputBar
                onSubmit={handleSubmit}
                disabled={inputDisabled}
                placeholder={placeholder}
                onChangeText={ctx.phase === "delivered" ? setFollowupText : undefined}
                onAttachmentsChange={ctx.stashAttachments}
              />
            </div>
          ) : null}
          <div className="text-center" style={{ marginTop: 15 }}>
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: "0.275px",
                lineHeight: "16.5px",
                color: "rgba(205,194,216,0.4)",
                textTransform: "uppercase",
              }}
            >
              BotWork agents can make mistakes. Please verify important information.
            </span>
          </div>
        </div>
      </main>

      {/* Agent profile panel — pushes the chat aside, not overlay.
          Keeps mounted through the slide-out animation, then unmounts. */}
      {profileData && profileAgent && (
        <aside
          className={`${profileClosing ? "app-anim-slide-out-right" : "app-anim-slide-right"} app-profile-aside`}
          style={{
            width: 513,
            flexShrink: 0,
            height: "100vh",
          }}
        >
          <AgentProfilePanel
            agent={profileData}
            onClose={closeProfile}
            onHire={() => {
              ctx.dispatch({ type: "SELECT_AGENT", agent: profileAgent });
              setBrowserOpen(false);
              closeProfile();
            }}
          />
        </aside>
      )}

      {browserOpen && (
        <div className="app-modal-backdrop" onClick={() => setBrowserOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <AgentBrowserModal
              onClose={() => setBrowserOpen(false)}
              onSelect={() => {
                if (ctx.task && ctx.task.topAgents[0]) {
                  ctx.dispatch({ type: "SELECT_AGENT", agent: ctx.task.topAgents[0] });
                }
                setBrowserOpen(false);
              }}
            />
          </div>
        </div>
      )}

      {ctx.loginOpen && (
        <div className="app-modal-backdrop" onClick={ctx.closeLogin}>
          <div onClick={(e) => e.stopPropagation()}>
            <LoginModal
              onSelect={async (method) => {
                if (method === "wallet") {
                  try {
                    await botwork.connectWallet();
                  } catch (err) {
                    toast.show(
                      err instanceof Error && err.message
                        ? err.message
                        : "Wallet connection failed.",
                    );
                    return;
                  }
                }
                ctx.signIn(method);
              }}
              onClose={ctx.closeLogin}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ReconnectBanner({ state }: { state: string }) {
  const message =
    state === "connecting"
      ? "Reconnecting to the P2P network…"
      : "Disconnected from the P2P network. Trying to reconnect…";
  const color = state === "connecting" ? "#F6AE2D" : "#EF4444";
  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 40,
        background: "rgba(30,30,30,0.95)",
        border: `1px solid ${color}`,
        color,
        borderRadius: 9999,
        padding: "6px 14px",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 12,
        fontWeight: 600,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      }}
    >
      {message}
    </div>
  );
}

function FollowupThread({ messages }: { messages: FollowupMessage[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
      {messages.map((m) =>
        m.role === "user" ? (
          <UserMessage key={m.id}>{m.text}</UserMessage>
        ) : m.thinking ? (
          <ThinkingIndicator key={m.id} label="Agent is typing…" />
        ) : (
          <SystemMessage key={m.id}>{m.text}</SystemMessage>
        ),
      )}
    </div>
  );
}

function SuggestionPill({ kind, onClick }: { kind: "approve" | "revise"; onClick: () => void }) {
  const label = kind === "approve" ? "Approve & pay?" : "Request revision?";
  const color = kind === "approve" ? "#4ADE80" : "#F6AE2D";
  const bg = kind === "approve" ? "rgba(74,222,128,0.1)" : "rgba(246,174,45,0.1)";
  return (
    <button
      onClick={onClick}
      className="app-anim-fade-up"
      style={{
        alignSelf: "flex-start",
        background: bg,
        border: `1px solid ${color}`,
        color: color,
        borderRadius: 9999,
        padding: "6px 14px",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 700,
        fontSize: 12,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>✨</span> {label}
    </button>
  );
}
